import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  HostListener,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { lastValueFrom, timeout } from 'rxjs';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { ComponentType } from '@angular/cdk/overlay';

import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { PopupService } from '../../../../shared/components/popup/popup.service';
import { FiletypesGuideDialogComponent } from '../../../../shared/components/filetypes-guide-dialog/filetypes-guide-dialog.component';
import { HelpInfoDialogComponent } from '../../../../shared/components/help-info-dialog/help-info-dialog.component';
import { ReferenceFilesDialogComponent } from '../../../../shared/components/reference-files-dialog/reference-files-dialog.component';
import { JobStatusDialogComponent } from '../../../../shared/components/job-status-dialog/job-status-dialog.component';
import { SubmitConfirmationDialogComponent } from '../../../../shared/components/submit-confirmation-dialog/submit-confirmation-dialog.component';
import { SimpleDialog } from '../../../../shared/models/simple-dialog';
import { Router } from '@angular/router';
// ── Services ────────────────────────────────────────────────────────────────
import { VendorService } from '../../../../shared/services/vendor.service';
import { S3FilesService } from '../../../../shared/services/s3files.service';
import { ProfileService } from '../../../auth/services/profile.service';
import { ScrollService } from '../../../../shared/services/scroll.service';
import {
  DateService,
  AppDateAdapter,
  APP_DATE_FORMATS,
} from '../../../../shared/services/date.service';
// ── Models & Config ─────────────────────────────────────────────────────────
import {
  IntakeFormFields,
  IntakeFile,
  IntakeFormAdditionalFields,
  TranslationParams,
  VERIFIED_VENDOR_SUBMISSION_PROTOCOL,
  VendorSubmissionStatus,
} from '../../../../shared/models/intake-form.model';
import {
  S3DestinationFolder,
  TranslationFileType,
} from '../../../../shared/models/s3-destination-folder';
import {
  defaultContentTypes,
  defaultTherapeuticAreas,
  defaultProducts,
  targetAudiences,
  documentTones,
  validFileTypes,
  validReferenceFileTypes,
  MAX_FILES_PER_ZONE,
  MAX_FILE_SIZE_BYTES,
  MAX_FILE_SIZE_ERROR_MESSAGE,
  requiredLangsConstants,
} from '../../../../shared/config/constants';
import { Language } from '../../../../shared/models/language.model';
import { BreadcrumbComponent } from '../../../../shared/components/breadcrumb/breadcrumb.component';
import { DatepickerInputComponent } from '../../../../shared/components/datepicker-input/datepicker-input.component';
import { LanguageDropdownComponent } from '../../../../shared/components/language-dropdown/language-dropdown.component';
import { MatIcon } from '@angular/material/icon';
import { HasUnsavedChanges } from '../../../auth/guards/has-unsaved-changes.interface';

export interface UploadedFile {
  file: File;
  name: string;
  size: number;
  sizeFormatted: string;
  status: 'ready' | 'error';
  errorMessage?: string;
}

/** Represents a form validation error for the error summary */
interface FormValidationError {
  field: string;
  controlName: string;
  message: string;
}

function jobNameValidator(control: AbstractControl): ValidationErrors | null {
  const forbidden = /[\\/:*?"',<>|{}]/;
  if (control.value && forbidden.test(control.value)) {
    return { invalidCharacters: true };
  }
  return null;
}

function languagePairValidator(
  group: AbstractControl,
): ValidationErrors | null {
  const from = group.get('translateFrom')?.value;
  const to: string[] = group.get('translateTo')?.value ?? [];
  if (from && Array.isArray(to) && to.includes(from)) {
    return { sameLanguage: true };
  }
  return null;
}

function minLengthArrayValidator(min: number) {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!Array.isArray(value) || value.length < min) {
      return { required: true };
    }
    return null;
  };
}

@Component({
  selector: 'app-vendor-translation',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatCheckboxModule,
    MatRadioModule,
    MatSelectModule,
    MatButtonModule,
    MatIcon,
    BreadcrumbComponent,
    DatepickerInputComponent,
    LanguageDropdownComponent,
  ],
  providers: [
    { provide: DateAdapter, useClass: AppDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: APP_DATE_FORMATS },
  ],
  templateUrl: './vendor-translation.component.html',
  styleUrl: './vendor-translation.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VendorTranslationComponent implements OnInit, HasUnsavedChanges {
  private readonly dialog = inject(MatDialog);
  private readonly popup = inject(PopupService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly vendorService = inject(VendorService);
  private readonly s3FilesService = inject(S3FilesService);
  private readonly profileService = inject(ProfileService);
  private readonly dateService = inject(DateService);
  private readonly scrollService = inject(ScrollService);

  // Dialog component references (exposed for template binding)
  readonly HelpInfoDialogComponent = HelpInfoDialogComponent;
  readonly FiletypesGuideDialogComponent = FiletypesGuideDialogComponent;
  readonly ReferenceFilesDialogComponent = ReferenceFilesDialogComponent;

  // ── Form state signals ──────────────────────────────────────────────────

  readonly submitted = signal(false);
  readonly isSubmitting = signal(false);
  private readonly verifiedSubmission = signal<{ jobUid?: string } | null>(null);
  readonly hasSubmissionAttempt = computed(() => this.verifiedSubmission() !== null);
  readonly formInvalid = signal(true);
  readonly validationErrors = signal<FormValidationError[]>([]);

  // ── Vendor access & profile signals ─────────────────────────────────────

  readonly hasVendorAccess = signal(false);
  readonly vendorsRetrieved = signal(false);
  readonly isCanadianUser = signal(false);

  // ── File upload signals ─────────────────────────────────────────────────

  readonly basicFiles = signal<UploadedFile[]>([]);
  readonly referenceFiles = signal<UploadedFile[]>([]);

  readonly basicDragOver = signal(false);
  readonly referenceDragOver = signal(false);
  readonly basicFilesTouched = signal(false);

  // ── Conditional UI signals ──────────────────────────────────────────────

  readonly showProductField = signal(false);
  readonly availableProductsList = signal<string[]>([]);
  readonly requiresDtp = signal<boolean>(false);

  readonly dtpWorkTypeOptions = [
    'PDF File Management',
    'Text inside images, graphics, screenshots that need translation',
    'Charts, diagrams, or visuals that need to be recreated',
    'Tables, forms, or multi\u2011column layouts',
    'Special formatting: callouts, text over images, custom fonts',
    'Right\u2011to\u2011left or non\u2011Latin script handling',
    'Not sure',
  ];

  /** Whether mandatory basic files are missing (shown after submit attempt) */
  readonly missingBasicFiles = computed(
    () => this.basicFiles().filter((f) => f.status === 'ready').length === 0,
  );

  readonly hasDuplicateFiles = computed(
    () =>
      this.basicFiles().some((f) => f.status === 'error') ||
      this.referenceFiles().some((f) => f.status === 'error'),
  );

  /** Selected source language signal (kept in sync via subscription) */
  readonly selectedSourceLanguage = signal('');

  /** Currently selected target Language objects for the multi-select dropdown */
  readonly selectedTargetLanguages = signal<Language[]>([]);

  /** Target language options excluding the currently selected source language */
  readonly availableTargetLanguages = computed(() => {
    const source = this.selectedSourceLanguage();
    return source
      ? this.languages().filter((lang) => lang.code !== source)
      : [...this.languages()];
  });
  readonly showCertificateEmailField = signal(false);
  readonly additionalInfoCharsRemaining = signal(1000);

  readonly contentTypes = defaultContentTypes;
  readonly therapeuticAreas = defaultTherapeuticAreas;
  readonly productsList = defaultProducts;
  readonly targetAudienceOptions = targetAudiences;
  readonly documentToneOptions = documentTones;

  readonly languages = signal<Language[]>([]);

  private readonly vendorPortalJobStatus = signal(
    this.resolveVendorPortalContext().jobStatus,
  );
  private readonly vendorPortalCancellationReason = signal(
    this.resolveVendorPortalContext().cancellationReason,
  );

  readonly showCancelledReason = computed(() => {
    return (
      this.vendorPortalJobStatus() === 'CANCELLED' &&
      this.vendorPortalCancellationReason().length > 0
    );
  });

  readonly cancelledReasonText = computed(() =>
    this.vendorPortalCancellationReason(),
  );

  readonly MAX_FILES_PER_ZONE = MAX_FILES_PER_ZONE;

  private resolveVendorPortalContext(): {
    jobStatus: string;
    cancellationReason: string;
  } {
    const state = history.state as {
      jobStatus?: string;
      cancellationReason?: string;
    } | null;

    const search = globalThis.window?.location.search ?? '';
    const params = new URLSearchParams(search);

    const jobStatus = state?.jobStatus ?? params.get('jobStatus') ?? '';
    const cancellationReason =
      state?.cancellationReason ?? params.get('cancelReason') ?? '';

    return {
      jobStatus: jobStatus.toUpperCase(),
      cancellationReason: cancellationReason.trim(),
    };
  }

  private readonly validFileExtRegex = new RegExp(
    `\\.(${validFileTypes.join('|')})$`,
    'i',
  );
  private readonly validReferenceFileExtRegex = new RegExp(
    `\\.(${validReferenceFileTypes.join('|')})$`,
    'i',
  );

  readonly minDate = this.dateService.calculateMinBusinessDate(2);
  readonly dateFilter = this.dateService.createWeekdayFilter(this.minDate);

  readonly quoteForm = new FormGroup(
    {
      jobName: new FormControl('', {
        nonNullable: true,
        validators: [
          Validators.required,
          Validators.maxLength(64),
          jobNameValidator,
        ],
      }),
      contentType: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      therapeuticArea: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      product: new FormControl('', { nonNullable: true }),
      targetAudience: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      documentToneOfVoice: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required],
      }),

      translateFrom: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      translateTo: new FormControl<string[]>([], {
        nonNullable: true,
        validators: [minLengthArrayValidator(1)],
      }),

      dateTranslationRequired: new FormControl<Date | null>(null, {
        validators: [Validators.required],
      }),
      translationCertificate: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      certificateEmailUser: new FormControl('', { nonNullable: true }),
      certificateEmailDomain: new FormControl('@astrazeneca.com', {
        nonNullable: true,
      }),

      additionalInformation: new FormControl('', {
        nonNullable: true,
        validators: [Validators.maxLength(1000)],
      }),

      dtpWorkTypes: new FormControl<string[]>([], { nonNullable: true }),
      dtpFiles: new FormControl<string[]>([], { nonNullable: true }),
    },
    { validators: languagePairValidator },
  );

  private readonly dtpValidatorEffect = effect(() => {
    const dtp = this.requiresDtp();
    const workTypesCtrl = this.quoteForm.get('dtpWorkTypes');
    const dtpFilesCtrl = this.quoteForm.get('dtpFiles');

    if (dtp) {
      workTypesCtrl?.setValidators([minLengthArrayValidator(1)]);
      dtpFilesCtrl?.setValidators([minLengthArrayValidator(1)]);
    } else {
      workTypesCtrl?.clearValidators();
      workTypesCtrl?.setValue([]);
      dtpFilesCtrl?.clearValidators();
      dtpFilesCtrl?.setValue([]);
    }
    workTypesCtrl?.updateValueAndValidity();
    dtpFilesCtrl?.updateValueAndValidity();
  });

  /** Auto-select the single ready file when DTP is enabled and only one file is uploaded */
  private readonly dtpAutoSelectEffect = effect(() => {
    const dtp = this.requiresDtp();
    const readyFiles = this.basicFiles().filter((f) => f.status === 'ready');

    if (!dtp || readyFiles.length !== 1) return;

    const ctrl = this.quoteForm.get('dtpFiles')!;
    const alreadySelected: string[] = ctrl.value ?? [];
    const fileName = readyFiles[0].name;

    if (!alreadySelected.includes(fileName)) {
      ctrl.setValue([fileName]);
      ctrl.markAsTouched();
    }
  });

  ngOnInit(): void {
    this.resetForm();
    this.checkVendorAvailability();
    this.loadUserProfile();
    this.loadLanguages();
    this.subscribeToFormChanges();
  }

  private checkVendorAvailability(): void {
    this.vendorService
      .getAvailableVendors()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (vendors: any[]) => {
          this.hasVendorAccess.set(vendors?.length > 0);
          this.vendorsRetrieved.set(true);

          if (!vendors?.length) {
            this.showErrorDialog(
              'No Vendors Available',
              'No suppliers are currently available to handle this request. Please try again later or contact support.',
            );
          }
        },
        error: () => {
          this.hasVendorAccess.set(false);
          this.vendorsRetrieved.set(true);
          this.showErrorDialog(
            'Service Unavailable',
            'Unable to check vendor availability. Please try again later or contact support.',
          );
        },
      });
  }

  private loadUserProfile(): void {
    this.profileService
      .getProfile()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((profile) => {
        this.isCanadianUser.set(!!profile.showCanadianForm);
      });
  }

  private async loadLanguages(): Promise<void> {
    const locales = await this.vendorService.getLocales(
      requiredLangsConstants.XTM,
    );

    this.languages.set(locales);
  }

  private subscribeToFormChanges(): void {
    // Source language → update signal + remove from target selections
    this.quoteForm
      .get('translateFrom')!
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this.selectedSourceLanguage.set(value);
        // Remove the source language from any current target selections
        const currentTargets: string[] =
          this.quoteForm.get('translateTo')?.value ?? [];
        if (currentTargets.includes(value)) {
          this.quoteForm
            .get('translateTo')
            ?.setValue(currentTargets.filter((lang) => lang !== value));
        }
      });

    this.quoteForm
      .get('therapeuticArea')!
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        const show = !!value && value !== 'Not applicable';
        this.showProductField.set(show);
        this.availableProductsList.set(
          show ? this.productsList.map((p) => p.value) : [],
        );
        const productControl = this.quoteForm.get('product');
        productControl?.setValue('');
        if (show) {
          productControl?.setValidators([Validators.required]);
        } else {
          productControl?.clearValidators();
        }
        productControl?.updateValueAndValidity();
      });

    // Translation Certificate → show/hide email field + validators
    this.quoteForm
      .get('translationCertificate')!
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this.showCertificateEmailField.set(value === 'yes');
        const userControl = this.quoteForm.get('certificateEmailUser');
        if (value === 'yes') {
          userControl?.setValidators([
            Validators.required,
            Validators.pattern(/^[a-zA-Z0-9._%+-]+$/),
          ]);
        } else {
          userControl?.clearValidators();
          userControl?.setValue('');
          this.quoteForm
            .get('certificateEmailDomain')
            ?.setValue('@astrazeneca.com');
        }
        userControl?.updateValueAndValidity();
      });

    // Additional Information → character counter
    this.quoteForm
      .get('additionalInformation')!
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this.additionalInfoCharsRemaining.set(1000 - (value?.length || 0));
      });

    // Form status → reactive invalid state + re-compute errors
    this.quoteForm.statusChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((status) => {
        this.formInvalid.set(status === 'INVALID');
      });
  }

  onSourceLanguageChange(lang: Language): void {
    this.selectedSourceLanguage.set(lang.code);
    this.quoteForm.get('translateFrom')?.setValue(lang.code);
    this.quoteForm.get('translateFrom')?.markAsTouched();

    // Clear target languages when source changes to avoid conflicts
    this.selectedTargetLanguages.set([]);
    this.quoteForm.get('translateTo')?.setValue([]);
  }

  onSourceLanguageClear(): void {
    this.selectedSourceLanguage.set('');
    this.quoteForm.get('translateFrom')?.setValue('');
    this.quoteForm.get('translateFrom')?.markAsTouched();
  }

  /**
   * Handle adding a single target language.
   */
  onTargetLanguageAdd(lang: Language): void {
    const current = this.selectedTargetLanguages();
    if (!current.some((l) => l.code === lang.code)) {
      const updated = [...current, lang];
      this.selectedTargetLanguages.set(updated);
      this.quoteForm.get('translateTo')?.setValue(updated.map((l) => l.code));
      this.quoteForm.get('translateTo')?.markAsTouched();
    }
  }

  /**
   * Handle "Select All" in the target language dropdown.
   */
  onTargetLanguagesSelectAll(langs: Language[]): void {
    this.selectedTargetLanguages.set([...langs]);
    this.quoteForm.get('translateTo')?.setValue(langs.map((l) => l.code));
    this.quoteForm.get('translateTo')?.markAsTouched();
  }

  /**
   * Handle removing a target language.
   */
  onTargetLanguageRemove(lang: Language): void {
    const updated = this.selectedTargetLanguages().filter(
      (l) => l.code !== lang.code,
    );
    this.selectedTargetLanguages.set(updated);
    this.quoteForm.get('translateTo')?.setValue(updated.map((l) => l.code));
  }

  // ── DTP checkbox helpers ─────────────────────────────────────────────

  toggleDtpWorkType(option: string, checked: boolean): void {
    const ctrl = this.quoteForm.get('dtpWorkTypes')!;
    const current: string[] = ctrl.value ?? [];
    ctrl.setValue(
      checked ? [...current, option] : current.filter((v) => v !== option),
    );
    ctrl.markAsTouched();
  }

  toggleDtpFile(fileName: string, checked: boolean): void {
    const ctrl = this.quoteForm.get('dtpFiles')!;
    const current: string[] = ctrl.value ?? [];
    ctrl.setValue(
      checked ? [...current, fileName] : current.filter((v) => v !== fileName),
    );
    ctrl.markAsTouched();
  }

  isDtpWorkTypeSelected(option: string): boolean {
    return (this.quoteForm.get('dtpWorkTypes')?.value ?? []).includes(option);
  }

  isDtpFileSelected(fileName: string): boolean {
    return (this.quoteForm.get('dtpFiles')?.value ?? []).includes(fileName);
  }

  // ── Job Name auto-resize ────────────────────────────────────────────────

  autoResizeJobName(event: Event): void {
    const textarea = event.target as HTMLTextAreaElement;
    const minHeight = 28;
    textarea.style.height = 'auto';
    const maxHeight = Number.parseInt(getComputedStyle(textarea).maxHeight, 10);
    textarea.style.height =
      Math.max(minHeight, Math.min(textarea.scrollHeight, maxHeight)) + 'px';
  }

  // ── File upload handlers ────────────────────────────────────────────────

  onDragOver(event: DragEvent, zone: 'basic' | 'reference'): void {
    event.preventDefault();
    event.stopPropagation();
    if (zone === 'basic') this.basicDragOver.set(true);
    else this.referenceDragOver.set(true);
  }

  onDragLeave(event: DragEvent, zone: 'basic' | 'reference'): void {
    event.preventDefault();
    event.stopPropagation();
    if (zone === 'basic') this.basicDragOver.set(false);
    else this.referenceDragOver.set(false);
  }

  onDrop(event: DragEvent, zone: 'basic' | 'reference'): void {
    event.preventDefault();
    event.stopPropagation();
    this.basicDragOver.set(false);
    this.referenceDragOver.set(false);

    const files = event.dataTransfer?.files;
    if (files) {
      this.processFiles(Array.from(files), zone);
    }
  }

  onFileSelected(event: Event, zone: 'basic' | 'reference'): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.processFiles(Array.from(input.files), zone);
      input.value = '';
    }
  }

  removeFile(zone: 'basic' | 'reference', index: number): void {
    this.getFileSignal(zone).update((list) =>
      list.filter((_, i) => i !== index),
    );
    this.revalidateZone(zone);
  }

  onGroupFocusOut(event: FocusEvent, controlName: string): void {
    const container = event.currentTarget as HTMLElement;
    const newFocus = event.relatedTarget as Node | null;
    if (!newFocus || !container.contains(newFocus)) {
      this.quoteForm.get(controlName)?.markAsTouched();
    }
  }

  isFieldInvalid(controlName: string): boolean {
    const control = this.quoteForm.get(controlName);
    if (!control) return false;
    return control.invalid && (control.touched || control.dirty);
  }

  onSubmit(): void {
    if (this.isSubmitting()) return;
    if (this.verifiedSubmission()) {
      const instance = this.openJobStatusDialog();
      void this.reconcileVerifiedSubmission(instance);
      return;
    }
    if (!this.hasVendorAccess()) {
      this.showErrorDialog(
        'No Vendors Available',
        'No suppliers are currently available. Please try again later or contact support.',
      );
      return;
    }

    this.submitted.set(true);
    this.quoteForm.markAllAsTouched();

    if (this.quoteForm.invalid) {
      setTimeout(() => {
        const errorSummary = document.querySelector(
          '.error-summary',
        ) as HTMLElement;
        if (errorSummary) {
          errorSummary.scrollIntoView({ behavior: 'smooth', block: 'start' });
          errorSummary.focus();
        }
      });
      return;
    }

    const allUploadedFiles = [...this.basicFiles(), ...this.referenceFiles()];
    if (allUploadedFiles.some((f) => f.status === 'error')) {
      return;
    }

    const readyBasicFiles = this.basicFiles().filter(
      (f) => f.status === 'ready',
    );
    if (readyBasicFiles.length === 0) {
      return;
    }

    // ── Show confirmation dialog before submitting ──────────────────────
    const confirmRef = this.dialog.open(SubmitConfirmationDialogComponent, {
      width: '622px',
      autoFocus: 'dialog',
      panelClass: 'no-padding-dialog',
    });

    confirmRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        if (this.isSubmitting() || this.verifiedSubmission()) return;
        const currentFiles = [...this.basicFiles(), ...this.referenceFiles()];
        if (this.quoteForm.invalid || currentFiles.some((file) => file.status === 'error')) return;
        const readyFiles = this.basicFiles().filter((file) => file.status === 'ready');
        if (readyFiles.length) this.submitIntakeForm(readyFiles);
      }
    });
  }

  /**
   * Perform the actual intake form submission after the user confirms.
   * Uploads a fixed file selection, then finalizes or follows the legacy flow.
   */
  private submitIntakeForm(readyBasicFiles: UploadedFile[]): void {
    this.isSubmitting.set(true);
    readyBasicFiles = readyBasicFiles.map((file) => ({ ...file }));
    const deferred = !this.isCanadianUser();
    const readyReferenceFiles = this.referenceFiles().filter((file) => file.status === 'ready')
      .map((file) => ({ ...file }));
    if (deferred) this.verifiedSubmission.set({});

    // ── Build the intake form payload matching the legacy API contract ───
    const formValue = this.quoteForm.getRawValue();

    const translationFiles: IntakeFile[] = [
      ...readyBasicFiles.map((f) => this.createFileObject(f, false)),
    ];

    const referenceFileObjects: IntakeFile[] = readyReferenceFiles
      .map((f) => this.createFileObject(f, true));

    // PDFs from source files are also considered reference files (legacy behaviour)
    const pdfAsReference = readyBasicFiles
      .filter((f) => this.isPdf(f.name))
      .map((f) => this.createFileObject(f, true));

    const allFiles = [
      ...translationFiles,
      ...referenceFileObjects,
      ...pdfAsReference,
    ];

    const filesNames = readyBasicFiles.map((f) => f.name).join(',');

    const deadlineStr = this.dateService.toISOStringOrEmpty(
      formValue.dateTranslationRequired,
    );

    const targetLanguages: string[] = formValue.translateTo ?? [];

    const translation: TranslationParams = {
      sourceLanguage: formValue.translateFrom,
      targetLanguage: targetLanguages.join(','),
      contentType: formValue.contentType,
      uploadType: 'file',
      jobName: formValue.jobName,
    };

    const additionalFields: IntakeFormAdditionalFields = {
      therapeuticArea: formValue.therapeuticArea,
      product: formValue.product,
      translationCertificateRequired:
        formValue.translationCertificate === 'yes',
      translationCertificateEmail:
        formValue.translationCertificate === 'yes'
          ? `${formValue.certificateEmailUser}${formValue.certificateEmailDomain}`
          : '',
      targetAudience: formValue.targetAudience,
      documentTone: formValue.documentToneOfVoice,
      deadline: deadlineStr,
      additionalInfo: formValue.additionalInformation,
      isDtpJob: true,
      isAdHocService: false,
      files: filesNames,
      dtpJobType: this.requiresDtp() ? formValue.dtpWorkTypes : [],
    };

    const formData: IntakeFormFields = {
      files: allFiles,
      translation,
      additionalFields,
      ...(deferred ? { submissionProtocol: VERIFIED_VENDOR_SUBMISSION_PROTOCOL } : {}),
    };

    const jobStatusInstance = this.openJobStatusDialog();
    const request = this.vendorService.sendIntakeFormRequest(formData, this.isCanadianUser());
    (deferred ? request.pipe(timeout(60000)) : request)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: async (response: any) => {
          if (deferred) {
            await this.completeVerifiedSubmission(response, readyBasicFiles, readyReferenceFiles, jobStatusInstance);
            return;
          }
          const {
            translationJobUids,
            files: failedFilesArray,
            error,
          } = response;

          if (error === 'NO_VENDORS_FOUND') {
            this.isSubmitting.set(false);
            jobStatusInstance.setErrorState(
              'No vendors were found to handle this request. Please contact support.',
            );
            return;
          }

          if (failedFilesArray?.length) {
            this.isSubmitting.set(false);
            jobStatusInstance.setErrorState(
              'Some files failed to process. Please try again or contact support.',
            );
            return;
          }

          if (translationJobUids?.length) {
            const jobUid = translationJobUids[0];
            await this.uploadFilesToS3(jobUid, readyBasicFiles, readyReferenceFiles);

            for (const pdfFile of readyBasicFiles.filter((f) => this.isPdf(f.name))) {
              try {
                await this.s3FilesService.enqueuePdfToDocxJob(jobUid, pdfFile.name);
              } catch (error) {
                console.error(`Error queueing PDF-to-DOCX conversion for "${pdfFile.name}":`, error);
              }
            }

            const filesOnS3 = await this.verifyUploadedFiles(TranslationFileType.SOURCE, jobUid, readyBasicFiles);
            const allRefUploadFiles = [...readyReferenceFiles, ...readyBasicFiles.filter((f) => this.isPdf(f.name))];
            const refFilesOnS3 = await this.verifyUploadedFiles(TranslationFileType.REFERENCE, jobUid, allRefUploadFiles);
            if (filesOnS3 && refFilesOnS3) {
              jobStatusInstance.setJobStatusInterval({
                translationJobUids, isCanadian: this.isCanadianUser(), isDtpJob: true, isAdhocService: false,
              });
              this.resetForm();
            } else {
              jobStatusInstance.setErrorState();
            }
            this.isSubmitting.set(false);
          } else {
            this.isSubmitting.set(false);
            jobStatusInstance.setErrorState(
              'Something went wrong while creating the quote request. Please try again.',
            );
          }
        },
        error: (err: unknown) => {
          console.error('Intake form submission error:', err);
          this.isSubmitting.set(false);
          if (deferred) {
            const rejectedBeforeCreation = err instanceof HttpErrorResponse && [400, 503].includes(err.status) &&
              ['INVALID_DEFERRED_SUBMISSION', 'SUBMISSION_TOO_LARGE', 'SUBMISSION_FINALIZATION_DISABLED',
                'PDF_ENQUEUE_CONFIGURATION_INVALID'].includes(err.error?.error);
            if (rejectedBeforeCreation) {
              this.verifiedSubmission.set(null);
              jobStatusInstance.setErrorState(
                'The request was rejected before job creation. Please check the selected files or contact support.',
              );
            } else {
              this.showUnconfirmedCreation(jobStatusInstance);
            }
            return;
          }
          jobStatusInstance.setErrorState(
            'Something went wrong while creating the quote request. Please try again or contact support.',
          );
        },
      });
  }

  private openJobStatusDialog(): JobStatusDialogComponent {
    // ── Open the job-status dialog (shows "Uploading…" immediately) ──────
    const jobStatusDialogRef = this.dialog.open(JobStatusDialogComponent, {
      width: '622px',
      disableClose: true,
      autoFocus: true,
      restoreFocus: false,
      panelClass: 'no-padding-dialog',
    });
    const jobStatusInstance = jobStatusDialogRef.componentInstance;

    // When the dialog closes, parse any failed files it returns
    jobStatusDialogRef.afterClosed().subscribe((failedFiles: any[]) => {
      if (failedFiles?.length) {
        this.parseFailedFiles(failedFiles);
      }

      //scroll to top only after the dialog is closed
      document
        .querySelector('.content')
        ?.scrollTo({ top: 0, behavior: 'smooth' });
    });

    return jobStatusInstance;
  }

  private async completeVerifiedSubmission(
    response: {
      translationJobUids?: unknown;
      submissionProtocol?: unknown;
      files?: unknown[];
      error?: string;
    },
    basicFiles: UploadedFile[],
    referenceFiles: UploadedFile[],
    instance: JobStatusDialogComponent,
  ): Promise<void> {
    const ids = response?.translationJobUids;
    const jobUid = Array.isArray(ids) && typeof ids[0] === 'string' && ids[0].trim() ? ids[0] : undefined;
    if (jobUid) this.verifiedSubmission.update((attempt) => attempt ? { ...attempt, jobUid } : null);
    try {
      if (response?.error === 'NO_VENDORS_FOUND' && !jobUid) {
        this.verifiedSubmission.set(null);
        instance.setErrorState('No vendors were found to handle this request. Please contact support.');
        return;
      }
      if (!jobUid) {
        this.showUnconfirmedCreation(instance);
        return;
      }
      if (!Array.isArray(ids) || ids.length !== 1 ||
        response.submissionProtocol !== VERIFIED_VENDOR_SUBMISSION_PROTOCOL ||
        response.error || !Array.isArray(response.files) || response.files.length) {
        console.error('Deferred creation response was not confirmed', { jobUid });
        instance.setErrorState(
          `Job ${jobUid}: creation was not fully confirmed. Do not create another quote; contact support.`,
          'Submission Status',
        );
        return;
      }
      try {
        await this.uploadFilesToS3(jobUid, basicFiles, referenceFiles, true);
      } catch (error) {
        console.error('Deferred submission upload failed', { jobUid, error });
        instance.setErrorState(
          `Job ${jobUid}: files could not be uploaded. Finalization and confirmation email were not requested. Do not create another quote; contact support.`,
        );
        return;
      }
      if (this.destroyRef.destroyed) return;
      try {
        const outcome = await lastValueFrom(this.vendorService.finalizeVendorSubmission(jobUid));
        this.presentVerifiedOutcome(jobUid, outcome, instance);
      } catch (error) {
        console.error('Deferred finalization outcome unconfirmed', { jobUid, error });
        await this.reconcileVerifiedSubmission(instance);
      }
    } finally {
      this.isSubmitting.set(false);
    }
  }

  private async reconcileVerifiedSubmission(instance: JobStatusDialogComponent): Promise<void> {
    const jobUid = this.verifiedSubmission()?.jobUid;
    if (!jobUid) {
      this.showUnconfirmedCreation(instance);
      return;
    }
    this.isSubmitting.set(true);
    try {
      const outcome = await lastValueFrom(this.vendorService.getVendorSubmissionStatus(jobUid));
      if (!this.destroyRef.destroyed) this.presentVerifiedOutcome(jobUid, outcome, instance);
    } catch (error) {
      console.error('Deferred submission status unavailable', { jobUid, error });
      instance.setErrorState(
        `Job ${jobUid}: the outcome could not be confirmed. Do not create another quote or resend confirmation; contact support. Submitting again only checks this job's status.`,
        'Submission Status',
      );
    } finally {
      this.isSubmitting.set(false);
    }
  }

  private presentVerifiedOutcome(
    jobUid: string,
    outcome: VendorSubmissionStatus,
    instance: JobStatusDialogComponent,
  ): void {
    if (this.destroyRef.destroyed) return;
    if (outcome?.jobUid !== jobUid || outcome.submissionProtocol !== VERIFIED_VENDOR_SUBMISSION_PROTOCOL) {
      console.error('Deferred submission status identity was not confirmed', { jobUid });
      instance.setErrorState(
        `Job ${jobUid}: an invalid status response was received. Do not create another quote; contact support.`,
        'Submission Status',
      );
      return;
    }
    if (outcome.status === 'ACCEPTED' && outcome.notification === 'SENT') {
      instance.setJobStatusInterval({
        translationJobUids: [jobUid], isDtpJob: true, verifiedSubmission: true,
      });
      this.resetForm();
      this.verifiedSubmission.set(null);
      return;
    }
    let message = 'Finalization has not been confirmed.';
    if (outcome.status === 'ACCEPTED') {
      message = outcome.notification === 'FAILED'
        ? 'Your request was accepted for processing, but confirmation email failed.'
        : 'Your request was accepted for processing, but confirmation email delivery is uncertain.';
    } else if (outcome.status === 'PENDING') {
      message = 'Uploads have not been verified.';
    } else if (outcome.status === 'FAILED') {
      message = 'Finalization failed; the existing job needs resolution.';
    } else if (outcome.status === 'FINALIZING') {
      message = 'Finalization is in progress; its outcome is not yet confirmed.';
    } else if (outcome.status === 'CANCELLED') {
      message = 'This request is cancelled.';
    }
    instance.setErrorState(
      `Job ${jobUid}: ${message} Do not create another quote for this request. Submitting again only checks this job's status.`,
      'Submission Status',
    );
  }

  private showUnconfirmedCreation(instance: JobStatusDialogComponent): void {
    instance.setErrorState(
      'Job creation could not be confirmed. A job may already exist. Do not submit another quote for this request; contact support.',
      'Submission Status',
    );
  }

  // ── S3 Upload Logic ─────────────────────────────────────────────────────
  private async uploadFilesToS3(
    jobUid: string,
    basicFiles: UploadedFile[],
    referenceFiles: UploadedFile[],
    stopOnError = false,
  ): Promise<void> {
    // Upload source files
    for (const uploadedFile of basicFiles) {
      try {
        const { url } = await this.s3FilesService.getPresignedUrlForFile(
          uploadedFile.name,
          S3DestinationFolder.TRANSLATION,
          false,
          jobUid,
          TranslationFileType.SOURCE,
        );
        await this.s3FilesService.uploadFile(url, uploadedFile.file);
      } catch (error) {
        console.error(
          `Error uploading source file "${uploadedFile.name}":`,
          error,
        );
        if (stopOnError) throw error;
      }
    }

    // Upload reference files (user-uploaded + PDF copies from source)
    if (referenceFiles.length > 0 || basicFiles.some((file) => this.isPdf(file.name))) {
      // Also add PDFs from source as reference
      const pdfSourceFiles = basicFiles.filter((f) => this.isPdf(f.name));
      const allRefFiles = [...referenceFiles, ...pdfSourceFiles];

      for (const refFile of allRefFiles) {
        try {
          const { url } = await this.s3FilesService.getPresignedUrlForFile(
            refFile.name,
            S3DestinationFolder.TRANSLATION,
            false,
            jobUid,
            TranslationFileType.REFERENCE,
          );
          await this.s3FilesService.uploadFile(url, refFile.file);
        } catch (error) {
          console.error(
            `Error uploading reference file "${refFile.name}":`,
            error,
          );
          if (stopOnError) throw error;
        }
      }
    }
  }

  private async verifyUploadedFiles(
    type: TranslationFileType,
    jobUid: string,
    expectedFiles: UploadedFile[],
  ): Promise<boolean> {
    if (expectedFiles.length === 0) return true;

    const expectedNames = [...new Set(expectedFiles.map((f) => f.name))].sort(
      (a, b) => a.localeCompare(b),
    );

    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        if (attempt > 0) {
          await new Promise((resolve) => setTimeout(resolve, 2000));
        }
        const response = await this.s3FilesService.getFilesInFolder(
          type,
          jobUid,
        );
        const actualNames = [...(response.files || [])].sort((a, b) =>
          a.localeCompare(b),
        );
        if (
          expectedNames.length === actualNames.length &&
          expectedNames.every((name, idx) => name === actualNames[idx])
        ) {
          return true;
        }
      } catch {
        // Retry on failure
      }
    }
    return false;
  }

  // ── Private helpers ─────────────────────────────────────────────────────

  private parseFailedFiles(files: any[]): void {
    files.forEach((file: any) => {
      const notEnoughCredits = 'NOT_ENOUGH_CREDITS';
      let fileErrorMessage = file.jobStatus || 'CANCELLED';

      if (file.cancelledReason === notEnoughCredits) {
        fileErrorMessage = notEnoughCredits;
      }

      console.warn(
        `File error: ${file.originalName ?? file.fileName} – ${fileErrorMessage}`,
      );
    });
  }

  private createFileObject(
    uploadedFile: UploadedFile,
    isReference: boolean,
  ): IntakeFile {
    const dtpFiles: string[] = this.quoteForm.get('dtpFiles')?.value ?? [];
    return {
      fileName: uploadedFile.name,
      originalName: uploadedFile.name,
      fileType: isReference ? 'attachment' : 'file',
      size: uploadedFile.size,
      isDtpFile: dtpFiles.includes(uploadedFile.name),
    };
  }

  private isPdf(fileName: string): boolean {
    return fileName.toLowerCase().endsWith('.pdf');
  }

  openDialog(
    event: Event,
    component: ComponentType<unknown>,
    width: string,
  ): void {
    event.preventDefault();
    this.dialog.open(component, { width, autoFocus: true });
  }

  private showErrorDialog(title: string, message: string): void {
    const ref = this.popup.open({
      title,
      headline: title,
      message,
      icon: { name: 'error', cssClass: 'error-icon' },
      width: '622px',
      maxWidth: '622px',
      hideButtons: true,
      centerContent: true,
      dialogTestId: 'vendor-translation-error',
    });

    // Always navigate back to the vendor-translation route when the popup closes
    ref.afterClosed().subscribe(() => {
      this.router.navigate(['/vendor-translation']);
    });
  }

  private processFiles(files: File[], zone: 'basic' | 'reference'): void {
    const fileSignal = this.getFileSignal(zone);
    const extensionRegex: RegExp =
      zone === 'reference'
        ? this.validReferenceFileExtRegex
        : this.validFileExtRegex;

    for (const file of files) {
      const currentValidCount = fileSignal().filter(
        (f) => f.status === 'ready',
      ).length;

      if (currentValidCount >= this.MAX_FILES_PER_ZONE) {
        fileSignal.update((list) => [
          ...list,
          {
            file,
            name: file.name,
            size: file.size,
            sizeFormatted: this.formatFileSize(file.size),
            status: 'error' as const,
            errorMessage: `Maximum ${this.MAX_FILES_PER_ZONE} files allowed.`,
          },
        ]);
        continue;
      }

      if (file.size > MAX_FILE_SIZE_BYTES) {
        fileSignal.update((list) => [
          ...list,
          {
            file,
            name: file.name,
            size: file.size,
            sizeFormatted: this.formatFileSize(file.size),
            status: 'error' as const,
            errorMessage: this.getMaxFileSizeErrorMessage(file.size),
          },
        ]);
        continue;
      }

      if (!extensionRegex.test(file.name)) {
        const extension =
          '.' + (file.name.split('.').pop()?.toLowerCase() || '');
        let errorMsg = 'This file is not supported.';
        if (zone === 'basic' && extension === '.pdf') {
          errorMsg = 'This file is not supported.';
        }
        fileSignal.update((list) => [
          ...list,
          {
            file,
            name: file.name,
            size: file.size,
            sizeFormatted: this.formatFileSize(file.size),
            status: 'error' as const,
            errorMessage: errorMsg,
          },
        ]);
        continue;
      }

      const isDuplicate = fileSignal().some(
        (existing) => existing.name.toLowerCase() === file.name.toLowerCase(),
      );
      if (isDuplicate) {
        fileSignal.update((list) => [
          ...list,
          {
            file,
            name: file.name,
            size: file.size,
            sizeFormatted: this.formatFileSize(file.size),
            status: 'error' as const,
            errorMessage: `A file named '${file.name}' has already been added. Please remove the existing file first if you wish to replace it.`,
          },
        ]);
        continue;
      }

      fileSignal.update((list) => [
        ...list,
        {
          file,
          name: file.name,
          size: file.size,
          sizeFormatted: this.formatFileSize(file.size),
          status: 'ready' as const,
        },
      ]);
    }
  }

  private revalidateZone(zone: 'basic' | 'reference'): void {
    const fileSignal = this.getFileSignal(zone);

    const extensionRegex: RegExp =
      zone === 'reference'
        ? this.validReferenceFileExtRegex
        : this.validFileExtRegex;

    fileSignal.update((list) => {
      let readyCount = 0;
      const seenNames = new Set<string>();

      return list.map((entry) => {
        // Re-run all validations in order for every file
        if (readyCount >= this.MAX_FILES_PER_ZONE) {
          return entry.status === 'error' &&
            entry.errorMessage ===
              `Maximum ${this.MAX_FILES_PER_ZONE} files allowed.`
            ? entry
            : {
                ...entry,
                status: 'error' as const,
                errorMessage: `Maximum ${this.MAX_FILES_PER_ZONE} files allowed.`,
              };
        }

        if (entry.size > MAX_FILE_SIZE_BYTES) {
          const sizeErrorMessage = this.getMaxFileSizeErrorMessage(entry.size);
          if (
            entry.status === 'error' &&
            entry.errorMessage === sizeErrorMessage
          ) {
            return entry;
          }
          return {
            ...entry,
            status: 'error' as const,
            errorMessage: sizeErrorMessage,
          };
        }

        if (!extensionRegex.test(entry.name)) {
          if (
            entry.status === 'error' &&
            entry.errorMessage === 'This file is not supported.'
          ) {
            return entry;
          }
          return {
            ...entry,
            status: 'error' as const,
            errorMessage: 'This file is not supported.',
          };
        }

        const nameLower = entry.name.toLowerCase();
        if (seenNames.has(nameLower)) {
          const dupMsg = `A file named '${entry.name}' has already been added. Please remove the existing file first if you wish to replace it.`;
          if (entry.status === 'error' && entry.errorMessage === dupMsg) {
            return entry;
          }
          return {
            ...entry,
            status: 'error' as const,
            errorMessage: dupMsg,
          };
        }
        seenNames.add(nameLower);

        readyCount++;
        if (entry.status === 'ready' && !entry.errorMessage) {
          return entry;
        }
        return { ...entry, status: 'ready' as const, errorMessage: undefined };
      });
    });
  }

  private getFileSignal(zone: 'basic' | 'reference') {
    switch (zone) {
      case 'basic':
        return this.basicFiles;
      case 'reference':
        return this.referenceFiles;
    }
  }

  private formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const value = bytes / Math.pow(k, i);
    const precision =
      Number.isInteger(value) || !Number.isInteger(Number(value.toFixed(2)))
        ? 2
        : 4;
    return parseFloat(value.toFixed(precision)) + ' ' + sizes[i];
  }

  private getMaxFileSizeErrorMessage(bytes: number): string {
    return `${MAX_FILE_SIZE_ERROR_MESSAGE} Selected file size: ${this.formatFileSize(bytes)}.`;
  }

  private resetForm(): void {
    this.quoteForm.reset({
      jobName: '',
      contentType: '',
      therapeuticArea: '',
      product: '',
      targetAudience: '',
      documentToneOfVoice: '',
      translateFrom: '',
      translateTo: [] as string[],
      dateTranslationRequired: null,
      translationCertificate: '',
      certificateEmailUser: '',
      certificateEmailDomain: '@astrazeneca.com',
      additionalInformation: '',
      dtpWorkTypes: [] as string[],
      dtpFiles: [] as string[],
    });
    this.basicFiles.set([]);
    this.referenceFiles.set([]);
    this.selectedSourceLanguage.set('');
    this.selectedTargetLanguages.set([]);
    this.submitted.set(false);
    this.showProductField.set(false);
    this.showCertificateEmailField.set(false);
    this.requiresDtp.set(false);
    this.additionalInfoCharsRemaining.set(1000);
    this.scrollService.scrollToTop();
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.hasUnsavedChanges()) {
      event.preventDefault();
    }
  }

  hasUnsavedChanges(): boolean {
    const formDirty = this.quoteForm.dirty;
    const hasFiles =
      this.basicFiles().length > 0 || this.referenceFiles().length > 0;
    return formDirty || hasFiles || this.hasSubmissionAttempt();
  }

  discardUnsavedChanges(): void {
    this.resetForm();
  }
}
